import React, { useState, useEffect } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { Helmet } from 'react-helmet'
import { useCart } from 'lib/core/hooks'
import { useCheckout, usePaymentMethod } from 'lib/modules/checkout'
import {
  affirmConfigQuery,
  AffirmConfig,
  setPaymentMethodOnCartMutation,
  SetPaymentMethodOnCartResponse
} from '../../utils'

export const AffirmMain = () => {
  const { loaded } = useAffirmLoaded()
  const { cart } = useCart()
  const { cartId, lockCheckout, unlockCheckout, placeOrder } = useCheckout()
  const { data } = useQuery<{ affirmConfig: AffirmConfig }>(affirmConfigQuery)

  const [setPaymentMethodOnCart] = useMutation<{
    setPaymentMethodOnCart: SetPaymentMethodOnCartResponse
  }>(setPaymentMethodOnCartMutation, {
    onCompleted: () => {
      unlockCheckout()
      placeOrder()
    },
    onError: () => {
      unlockCheckout()
    }
  })

  const config = data?.affirmConfig

  useEffect(() => {
    if (loaded) {
      unlockCheckout()
    } else {
      lockCheckout()
    }
    // eslint-disable-next-line
  }, [
    loaded
  ])

  usePaymentMethod(() => {
    const cartData = cart?.data
    if (!cartData) {
      return
    }

    lockCheckout()
    // @ts-expect-error
    window.affirm.checkout({
      merchant: {
        user_cancel_url: `${config?.pwa_url}affirm-cancel`,
        user_confirmation_url: `${config?.pwa_url}affirm-confirm`,
        user_confirmation_url_action: 'GET',
        merchant: process.env.REACT_APP_TITLE ?? ''
      },
      shipping: {
        name: {
          first: cartData.shippingAddress?.firstName ?? '',
          last: cartData.shippingAddress?.lastName ?? ''
        },
        address: {
          line1: cartData.shippingAddress?.street ?? '',
          line2: cartData.shippingAddress?.street2 ?? '',
          city: cartData.shippingAddress?.city ?? '',
          state: cartData.shippingAddress?.regionCode ?? '',
          country: cartData.shippingAddress?.countryCode ?? '',
          zipcode: cartData.shippingAddress?.postalCode ?? ''
        },
        email: cartData.email ?? '',
        phone_number: cartData.shippingAddress?.phoneNumber ?? ''
      },
      billing: {
        name: {
          first: cartData.billingAddress?.firstName ?? '',
          last: cartData.billingAddress?.lastName ?? ''
        },
        address: {
          line1: cartData.billingAddress?.street ?? '',
          line2: cartData.billingAddress?.street2 ?? '',
          city: cartData.billingAddress?.city ?? '',
          state: cartData.billingAddress?.regionCode ?? '',
          country: cartData.billingAddress?.countryCode ?? '',
          zipcode: cartData.billingAddress?.postalCode ?? ''
        },
        email: cartData.email ?? '',
        phone_number: cartData.billingAddress?.phoneNumber ?? ''
      },
      metadata: {
        mode: 'modal'
      },
      items: cartData.items.map(cartItem => {
        return {
          sku: cartItem.product.sku,
          display_name: cartItem.product.name,
          unit_price: cartItem.prices.row_total.value * 100,
          qty: cartItem.quantity
        }
      }),
      total: cartData.prices.grandTotal.value * 100
    })

    // @ts-expect-error
    window.affirm.checkout.open({
      onFail: function (e: any) {
        unlockCheckout()
      },
      onSuccess: function (e: any) {
        setPaymentMethodOnCart({
          variables: {
            cartId,
            checkoutToken: e.checkout_token
          }
        })
      }
    })
  })

  return (
    <div>
      {config?.api_key_public && config.api_url && (
        <Helmet>
          <script>
            {`
              window._affirm_config = {
                public_api_key: "${config?.api_key_public}",
                script: "${config.api_url}/js/v2/affirm.js",
              };
              (function(m,g,n,d,a,e,h,c){var b=m[n]||{},k=document.createElement(e),p=document.getElementsByTagName(e)[0],l=function(a,b,c){return function(){a[b]._.push([c,arguments])}};b[d]=l(b,d,"set");var f=b[d];b[a]={};b[a]._=[];f._=[];b._=[];b[a][h]=l(b,a,h);b[c]=function(){b._.push([h,arguments])};a=0;for(c="set add save post open empty reset on off trigger ready setProduct".split(" ");a<c.length;a++)f[c[a]]=l(b,d,c[a]);a=0;for(c=["get","token","url","items"];a<c.length;a++)f[c[a]]=function(){};k.async=
              !0;k.src=g[e];p.parentNode.insertBefore(k,p);delete g[e];f(g);m[n]=b})(window,_affirm_config,"affirm","checkout","ui","script","ready","jsReady");
            `}
          </script>
        </Helmet>
      )}
    </div>
  )
}

export const useAffirmLoaded = () => {
  const [loaded, setLoaded] = useState(false)
  const [intervalId, setIntervalId] = useState<number | undefined>(undefined)

  // Define the interval that will check if the script loaded
  useEffect(() => {
    const id = window.setInterval(() => setLoaded(scriptIsLoaded()), 500)
    setIntervalId(id)
  }, [
    setLoaded,
    setIntervalId
  ])

  // Clear the interval
  useEffect(() => {
    if (!intervalId || !loaded) {
      return
    }

    window.clearInterval(intervalId)

    return () => {
      intervalId && window.clearInterval(intervalId)
    }
  }, [
    intervalId,
    loaded
  ])

  return {
    loaded
  }
}

// @ts-expect-error
const scriptIsLoaded = () => Boolean(window.affirm?.checkout)
